import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CancelIcon from "@mui/icons-material/Cancel";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Collapse, Divider, Stack } from "@mui/material";
import { useTheme } from "@/contexts/theme";
import { DownIcon, UpIcon } from "./icons";
import CircularProgress from "@mui/material/CircularProgress";
import { FONT_FAMILY } from "@/utils/font-family";
import { PlusIcon } from "@/icons";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

const StyledAutocomplete: any = styled(Autocomplete, {
  shouldForwardProp: (prop) =>
    prop !== "selectedOption" && prop !== "direction",
})((props: any) => {
  const dropdownHeight =
    props?.style?.height ?? "var(--theme-dropdown-height, 40px)";
  // When height is "auto" (multi-select), keep a real min height so the
  // inner OutlinedInput does not collapse inside the Autocomplete root.
  const dropdownMinHeight =
    props?.style?.minHeight ??
    (dropdownHeight === "auto"
      ? "var(--theme-dropdown-height, 40px)"
      : dropdownHeight);

  return {
    direction: props?.direction,
    boxSizing: "border-box",
    borderRadius:
        props?.style?.borderRadius ??
        "var(--theme-dropdown-border-radius, 4px)",
    height: dropdownHeight,
    minHeight: props?.style?.minHeight ?? dropdownMinHeight,
    fontFamily: "Lexend",
    fontStyle: "normal",
    fontWeight: 300,
    fontSize: 14,
    lineHeight: "21px",
    display: "flex",
    alignItems: "center",
    color: props?.error ? "red" : "#000000",
    backgroundColor: "#FFF",
    boxShadow: "var(--theme-dropdown-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08))",
    "& .MuiOutlinedInput-root": {
      paddingRight: "9px!important",
      color: props?.error ? "red" : "",
      height: dropdownHeight,
      minHeight: dropdownMinHeight,
      boxSizing: "border-box",
      alignItems: "center",
      fontFamily: "Heebo",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: 14,
      width: "100%",
      "& fieldset": {
        border: props?.selectedOption
          ? `1px solid ${
              props?.style?.borderColor ||
              "var(--theme-dropdown-border-color, transparent)"
            } !important`
          : props?.style?.borderColor
            ? `1px solid ${props?.style?.borderColor} !important`
            : "var(--theme-dropdown-border, none) !important",
        borderColor: `${
          props?.style?.borderColor ||
          "var(--theme-dropdown-border-color, transparent)"
        } !important`,
        boxSizing: "border-box",
        borderRadius: "var(--theme-dropdown-border-radius, 4px)",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: props?.selectedOption
          ? `1px solid ${
              props?.style?.borderColor ||
              "var(--theme-dropdown-border-color, transparent)"
            }`
          : props?.style?.borderColor
            ? `1px solid ${props?.style?.borderColor}`
            : "var(--theme-dropdown-border, none)",
        borderColor: `${
          props?.style?.borderColor ||
          "var(--theme-dropdown-border-color, transparent)"
        } !important`,
        borderRadius: "var(--theme-dropdown-border-radius, 4px)",
      },
      "&.Mui-focused fieldset": {
        borderColor: props?.style?.borderColor
          ? `${props?.style?.borderColor} !important`
          : "var(--theme-dropdown-border-color, transparent) !important",
        borderWidth: "1px !important",
        borderStyle: "solid !important",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: props?.style?.borderColor
          ? `${props?.style?.borderColor} !important`
          : "var(--theme-dropdown-border-color, transparent) !important",
        borderWidth: "1px !important",
        borderStyle: "solid !important",
      },
      "&:hover fieldset": {
        borderColor: props?.style?.borderColor
          ? `${props?.style?.borderColor} !important`
          : "var(--theme-dropdown-border-color, transparent) !important",
        borderWidth: "1px !important",
        borderStyle: "solid !important",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: props?.style?.borderColor
          ? `${props?.style?.borderColor} !important`
          : "var(--theme-dropdown-border-color, transparent) !important",
        borderWidth: "1px !important",
        borderStyle: "solid !important",
      },
      "& .MuiAutocomplete-endAdornment": {
        right: props?.direction === "rtl" ? "auto" : 0,
        left: props?.direction === "rtl" ? 0 : "auto",
        marginRight: props?.direction === "rtl" ? "0" : "4px",
        marginLeft: props?.direction === "rtl" ? "4px" : "0",
      },

      "& .MuiAutocomplete-input": {
        padding: 0,
        direction: props?.direction,
        flex: 1,
        "&.Mui-disabled": {
          cursor: "not-allowed",
          color: "#000",
          WebkitTextFillColor: "#000",
          opacity: 1,
        },
      },
      "& input::placeholder": {
        padding: "0px",
        color: "var(--theme-dropdown-placeholder-color, #B1B1B1)",
        opacity: 1,
        fontFamily: "var(--theme-font-family-base, Heebo)",
        fontStyle: "normal",
        fontWeight: "var(--theme-dropdown-placeholder-weight, 500)",
        fontSize: "var(--theme-dropdown-placeholder-size, 12px)",
      },
    },
    ...props?.style,
  };
});

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: "black",
  backgroundColor: "white",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  justifyContent: "space-between",
  fontFamily: "var(--theme-font-family-base, Heebo)",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "20px",
}));

const GroupItems = styled("ul")({
  padding: 0,
  listStyleType: "none",
});

// Inside your GoMakeAutoComplate function
const GoMakeAutoComplate = ({
  value,
  onChange,
  onInputChange,
  onKeyDown,
  onClose,
  style,
  error,
  options,
  autoHighlight,
  getOptionLabel,
  renderOption,
  disableClearable,
  placeholder,
  defaultValue,
  disabled,
  multiple = false,
  onChangeTextField,
  PaperComponent,
  PopperComponent,
  withArrow = false,
  groupBy,
  popupIcon,
  renderGroup,
  loading, // Add loading prop
  defaultFiltering = true,
  startIcon,
  arrowIcon,
  open, // Add the `open` prop
  onOpen, // Add the `onOpen` prop
  noOptionsText,
  componentsProps,
  isOptionEqualToValue,
  labelText,
  getOptionDisabled,
  disablePortal,
  sx,
  renderTags: renderTagsFromProps,
}: {
  value?: any;
  onChange?: any;
  onInputChange?: any;
  onKeyDown?: any;
  onClose?: any;
  style?: any;
  error?: any;
  options: any;
  autoHighlight?: any;
  getOptionLabel?: any;
  renderOption?: any;
  disableClearable?: any;
  placeholder?: any;
  defaultValue?: any;
  multiple?: any;
  disabled?: any;
  onChangeTextField?: any;
  PaperComponent?: any;
  PopperComponent?: any;
  popupIcon?: any;
  withArrow?: boolean;
  renderGroup?: (params: any) => React.ReactNode;
  groupBy?: (option: any) => string;
  loading?: boolean; // Add loading prop
  defaultFiltering?: boolean;
  startIcon?: React.ReactNode;
  arrowIcon?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  noOptionsText?: string;
  componentsProps?: any;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  labelText?: string;
  getOptionDisabled?: (option: any) => boolean;
  freeSolo?: boolean;
  inputValue?: string;
  disablePortal?: boolean;
  sx?: any;
  renderTags?: (
    tagValue: any[],
    getTagProps: (args: { index: number }) => Record<string, unknown>,
  ) => ReactNode;
}) => {
  const [selectedOption, setSelectedOption] = useState<any>();
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const isRTL = dir === "rtl";
  const { primaryColor, neutralColor } = useGomakeTheme();
  const { getIconOverride, currentTheme } = useTheme();
  const CloseIconComponent = getIconOverride("CloseIcon");
  const isHpClient = currentTheme === "hp-client";
  const isModern = currentTheme === "modern";

  useEffect(() => {
    if ((Array.isArray(value) && value.length > 0) || !Array.isArray(value)) {
      setSelectedOption(value);
    } else {
      setSelectedOption(null);
    }
  }, [value]);

  const [expandedGroups, setExpandedGroups] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const optionsArray = Array.isArray(options) ? options : [];
  const groupByFunction =
    typeof groupBy === "function" ? groupBy : (option) => option;
  const uniqueGroups = Array.from(new Set(optionsArray.map(groupByFunction)));
  const lastGroup = uniqueGroups[uniqueGroups.length - 1];

  const getOptionText = (option: any) => {
    if (typeof getOptionLabel === "function") {
      return getOptionLabel(option);
    }
    if (typeof option === "string") return option;
    return (
      option?.label ??
      option?.text ??
      option?.name ??
      String(option?.value ?? "")
    );
  };

  const resolvedRenderOption = (props: any, option: any) => {
    const isAddLanguage = option?.value === "add_language_action";
    if (!isAddLanguage) {
      return renderOption ? (
        renderOption(props, option)
      ) : (
        <li {...props}>{getOptionText(option)}</li>
      );
    }

    return (
      <li
        {...props}
        style={{
          ...props?.style,
          padding: 0,
          position: "sticky",
          bottom: 0,
          backgroundColor: "#FFFFFF",
          zIndex: 1,
        }}
      >
        <Stack
          direction={isRTL ? "row-reverse" : "row"}
          alignItems="center"
          justifyContent="center"
          gap="8px"
          sx={{
            width: isModern ? "100%" : "calc(100% - 16px)",
            marginInline: isModern ? undefined : "8px",
            paddingBlock: "8px",
            paddingInline: isModern ? undefined : "10px",
            borderRadius: "6px",
            backgroundColor: primaryColor(500),
            color: "#FFFFFF",
            fontWeight: 600,
            fontFamily: "Heebo",
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
          }}
        >
          <PlusIcon color="#FFFFFF" />
          {getOptionText(option)}
        </Stack>
      </li>
    );
  };

  /** HP: Tangram-style tags; dismiss only (HpCloseIcon 16px). No leading plus — chips are selected values, not “add”. */
  const hpAutocompleteRenderTags =
    multiple && isHpClient
      ? (tagValue: any[], getTagProps: any) =>
          tagValue.map((option: any, index: number) => (
            <Chip
              {...getTagProps({ index })}
              label={getOptionText(option)}
              deleteIcon={
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                  }}
                >
                  {CloseIconComponent ? (
                    <CloseIconComponent
                      width={16}
                      height={16}
                      color="var(--theme-autocomplete-chip-fg, #0E3D7A)"
                    />
                  ) : (
                    <CancelIcon
                      sx={{
                        fontSize: 18,
                        color: "var(--theme-autocomplete-chip-fg, #0E3D7A)",
                      }}
                    />
                  )}
                </Box>
              }
              sx={{
                height: "var(--theme-autocomplete-chip-height, 32px)",
                minHeight: "var(--theme-autocomplete-chip-height, 32px)",
                maxHeight: "var(--theme-autocomplete-chip-height, 32px)",
                padding: "var(--theme-autocomplete-chip-padding, 6px 10px)",
                gap: "var(--theme-autocomplete-chip-gap, 4px)",
                backgroundColor: "var(--theme-autocomplete-chip-bg, #E5F4FF)",
                borderRadius: "var(--theme-autocomplete-chip-radius, 6px)",
                color: "var(--theme-autocomplete-chip-fg, #0E3D7A)",
                fontFamily:
                  "var(--theme-font-family-base, 'Forma DJR UI', sans-serif)",
                fontWeight: 400,
                fontSize: 16,
                lineHeight: "20px",
                border: "none",
                "& .MuiChip-label": {
                  paddingLeft: "4px",
                  paddingRight: "4px",
                },
                "& .MuiChip-deleteIcon": {
                  margin: 0,
                  marginLeft: "4px",
                  width: 20,
                  height: 20,
                },
              }}
            />
          ))
      : undefined;

  const resolvedRenderTags = renderTagsFromProps ?? hpAutocompleteRenderTags;

  return (
    <>
      {labelText && (
        <div style={{ ...FONT_FAMILY.Lexend(600, 14) }}>{labelText}</div>
      )}
      <StyledAutocomplete
        {...(value !== undefined ? { value } : {})}
        {...(selectedOption && { selectedOption })}
        {...(selectedOption && { selectedOption })}
        direction={dir}
        onChange={(e: any, value: any) => {
          onChange(e, value);
          setSelectedOption(value);
        }}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        open={open} // Pass the `open` prop to Autocomplete
        onOpen={onOpen} // Pass the `onOpen` callback to Autocomplete
        onClose={onClose}
        style={style}
        options={options}
        disabled={disabled}
        //freeSolo={freeSolo}
        popupIcon={
          withArrow ? popupIcon ? popupIcon : <ArrowDropDownIcon /> : ""
        }
        groupBy={groupBy}
        renderGroup={(params) => (
          <div key={params.key}>
            <GroupHeader onClick={() => toggleGroup(params.group)}>
              {params.group}
              {expandedGroups[params.group] ? <UpIcon /> : <DownIcon />}
            </GroupHeader>
            {params.group !== lastGroup && (
              <Divider
                sx={{
                  width: "97%",
                  margin: "0 auto",
                  borderColor: "#ECECEC",
                  my: 1,
                }}
              />
            )}
            <Collapse in={expandedGroups[params.group]}>
              <GroupItems>{params.children}</GroupItems>
            </Collapse>
          </div>
        )}
        renderInput={(params: any) => (
          <TextField
            {...params}
            placeholder={defaultValue?.label || placeholder}
            onChange={onChangeTextField || params.onChange}
            InputProps={{
              ...params.InputProps,
              ...(startIcon && {
                startAdornment: (
                  <div style={{ display: "flex", marginLeft: "10px" }}>
                    {startIcon}
                  </div>
                ),
              }),
              ...(loading && {
                endAdornment: (
                  <CircularProgress color="inherit" size={20} sx={{ ml: 2 }} />
                ),
              }),
            }}
          />
        )}
        loading={loading} // Pass loading prop to show spinner
        defaultValue={defaultValue}
        autoHighlight={autoHighlight}
        getOptionLabel={getOptionLabel}
        renderOption={resolvedRenderOption}
        error={error}
        disableClearable={disableClearable}
        placeholder="Enter"
        multiple={multiple}
        getOptionSelected={(option: any, value: any) =>
          option?.id === value?.id
        }
        PaperComponent={PaperComponent}
        PopperComponent={PopperComponent}
        disablePortal={disablePortal}
        filterOptions={defaultFiltering ? undefined : (x) => x} //? Bypass default filtering
        noOptionsText={noOptionsText}
        componentsProps={componentsProps}
        isOptionEqualToValue={
          isOptionEqualToValue ??
          ((option: any, value: any) =>
            String(option?.id) === String(value?.id))
        }
        getOptionDisabled={getOptionDisabled}
        sx={sx}
        {...(resolvedRenderTags ? { renderTags: resolvedRenderTags } : {})}
      />
    </>
  );
};

export { GoMakeAutoComplate };
